import { Link } from 'react-router-dom';
import { QUESTIONS } from '../data/questions';

interface CheckIntroProps {
  onStart: () => void;
}

export function CheckIntro({ onStart }: CheckIntroProps) {
  const counts = QUESTIONS.reduce<Record<string, number>>((acc, q) => {
    acc[q.type] = (acc[q.type] ?? 0) + 1;
    return acc;
  }, {});

  const categories = [
    {
      key: 'phishing',
      icon: 'ri-mail-forbid-fill',
      label: 'Phishing',
      blurb: 'Deceptive emails impersonating brands, colleagues or services to steal credentials, money, or access.',
    },
    {
      key: 'smishing',
      icon: 'ri-message-2-line',
      label: 'Smishing',
      blurb: 'SMS scams, fake delivery fees, bank alerts, two-factor prompts, designed for a quick, distracted tap.',
    },
    {
      key: 'vishing',
      icon: 'ri-phone-line',
      label: 'Vishing',
      blurb: 'Voice-call attacks pretending to be IT, your bank, or a vendor to extract codes, payments, or remote access.',
    },
  ];

  return (
    <section className="relative pt-32 pb-20">
      <div className="container mx-auto px-6 max-w-4xl text-center">
        <div className="inline-flex items-center gap-2 border border-blue-500/30 rounded-full px-4 py-1 mb-8">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          <span className="text-sm text-blue-300">Free · ~3 minutes · no signup required</span>
        </div>

        <h1 className="text-5xl md:text-6xl font-light mb-6 tracking-tight text-white">
          How well can you spot a
          <br />
          <span className="text-blue-400 font-normal">social engineering attack?</span>
        </h1>

        <p className="text-xl mb-12 max-w-2xl mx-auto font-light text-gray-400">
          A quick scenario-based check across phishing, smishing and vishing. You'll see {QUESTIONS.length} real-world examples and decide whether each one is safe or suspicious.
        </p>

        <div className="grid md:grid-cols-3 gap-4 mb-12 text-left">
          {categories.map((cat) => (
            <div
              key={cat.key}
              className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                  <i className={`${cat.icon} text-xl text-blue-400`}></i>
                </div>
                <div>
                  <h3 className="text-white font-medium">{cat.label}</h3>
                  <p className="text-xs text-gray-500">{counts[cat.key] ?? 0} scenarios</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">{cat.blurb}</p>
            </div>
          ))}
        </div>

        <button
          onClick={onStart}
          className="group px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 cursor-pointer bg-white text-black hover:bg-blue-400 hover:text-white"
        >
          Start the check
          <span className="ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block">→</span>
        </button>

        <p className="mt-6 text-xs text-gray-500">
          Your answers stay in this browser. We don't track them or tie them to you. By
          starting, you agree to our{' '}
          <Link to="/terms" className="text-blue-400 hover:text-blue-300 underline">
            Terms of Service
          </Link>
          .
        </p>
      </div>
    </section>
  );
}
